import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LabeledInputComponent } from "../../../../shared/components/labeled-input/labeled-input.component";
import { PrimaryButtonComponent } from "../../../../shared/components/primary-button/primary-button.component";
import { LogoComponent } from "../../../../shared/components/logo/logo.component";
import { SecondaryButtonComponent } from "../../../../shared/components/secondary-button/secondary-button.component";
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { TrimEmailPipe } from '../../../../core/pipes/trim-email.pipe';
import { combineLatest, firstValueFrom } from 'rxjs';
import { AuthService } from '../../../../core/api/auth.service';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { NavigationShareService } from '../../../../core/services/navigation-share.service';
import { AuthValidatorService } from '../../../../core/services/validators/auth-validator.service';

@Component({
  selector: 'app-temp-password',
  standalone: true,
  imports: [LabeledInputComponent, PrimaryButtonComponent, LogoComponent, SecondaryButtonComponent, TranslocoPipe, ToastComponent, RouterLink],
  providers: [TrimEmailPipe],
  templateUrl: './temp-password.component.html',
  styleUrl: './temp-password.component.css'
})
export class TempPasswordComponent implements OnInit {
  // Email provided as a query parameter in the URL (e.g., /temp-password?email=email)
  email: string | null = null;

  promotionCode: string | undefined = undefined;

  // Email Inputs
  emailInput: string = '';
  inputError: string | null = null;
  invalidEmailErrorMessage: string = `Please enter a valid email address`;

  // Password Inputs
  passwordInput: string = '';
  passwordInputError: string | null = null;
  invalidPasswordErrorMessage: string = `Please enter a valid password`;

  continueClicked: boolean = false;

  @ViewChild(ToastComponent) toast!: ToastComponent;

  constructor(private route: ActivatedRoute, private router: Router, private translocoService: TranslocoService, private trimEmailPipe: TrimEmailPipe, private authService: AuthService, private shareService: NavigationShareService, private authValidator: AuthValidatorService) { }


  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.email = params.get('email');
      if (!this.email) {
        this.router.navigate(['/auth/signup']);
        return;
      }
      this.emailInput = this.trimEmailPipe.transform(this.email)

      var promotionCode = params.get('promotion') || undefined;
      if (promotionCode) {
        promotionCode = this.trimEmailPipe.transform(promotionCode)
      }
      this.promotionCode = promotionCode;
    });

    // Populate with translation
    combineLatest([
      this.translocoService.selectTranslate('auth.invalid_email'),
      this.translocoService.selectTranslate('auth.invalid_password'),
    ]).subscribe(([invalidEmail, invalidPassword]) => {
      this.invalidEmailErrorMessage = invalidEmail;
      this.invalidPasswordErrorMessage = invalidPassword;
    });
  }

  resendEmailButton() {
    this.router.navigate(['/auth/signup'], { queryParams: { email: this.emailInput, promotion: this.promotionCode } });
  }

  async continueButtonClick() {
    // Prevent multiple clicks
    if (this.continueClicked) {
      return;
    }
    this.continueClicked = true;

    const passwordInputValue = this.passwordInput.trim();

    // Validate input
    const passwordError = this.authValidator.validateTempPassword(passwordInputValue);
    if (passwordError) {
      this.passwordInputError = passwordError;
      this.continueClicked = false;
      return;
    }

    // Verify password
    let verifyTempPasswordResult = undefined;
    try {
      verifyTempPasswordResult = await firstValueFrom(this.authService.verifyTempPassword(this.emailInput, passwordInputValue));
    }
    catch (error) {
      this.toast.showToast("shared.error", "shared.toast.credentials_error", 'error', 3000);
      this.passwordInputError = '';
      this.continueClicked = false;
      return;
    }

    this.passwordInputError = '';
    this.continueClicked = false;

    this.shareService.setState("roles", verifyTempPasswordResult.roles);
    this.shareService.setState("email", this.emailInput);

    this.router.navigate(['/auth/onboarding'], { queryParams: { step: 1, promotion: this.promotionCode } });
  }
}
